import { t } from "@travily/i18n";
import type CelebrationsRow from "@travily/types/models/CelebrationsRow";
import type Program from "@travily/types/models/ProgramsCard";
import type {
  ProgramsCardAccountWithMembersTrait,
  ProgramsCardAvailabilityTrait,
  ProgramsCardDescriptionTrait,
  ProgramsCardLocationsTrait,
  ProgramsCardMapLocationsTrait,
  ProgramsCardPhotosTrait,
} from "@travily/types/models/ProgramsCard";
import type ImageType from "@travily/types/shared/Image";
import { ActionPill } from "@ui/components/ActionPill";
import { Icon } from "@ui/components/Icon";
import { RichText } from "@ui/components/RichText";
import { SectionJumpBar } from "@ui/components/SectionJumpBar";
import { SectionJumpNav, type SectionJumpSection } from "@ui/components/SectionJumpNav";
import { TopBarScrollShadow } from "@ui/components/TopBar/TopBarScrollShadow";
import { useBreakpoint } from "@ui/hooks/use-breakpoint";
import { useHasMounted } from "@ui/hooks/use-has-mounted";
import { useScrollSentinel } from "@ui/hooks/use-scroll-sentinel";
import { useScrollSpy } from "@ui/hooks/use-scroll-spy";
import type { ShareMethod } from "@ui/hooks/use-share";
import { tv } from "@ui/lib/tv";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { useCallback, useRef } from "react";
import { AvailabilityCard } from "./components/AvailabilityCard";
import { CelebrationsCard } from "./components/CelebrationsCard";
import { Highlights } from "./components/Highlights";
import { hasKnownInfluences, InfluencesCard } from "./components/InfluencesCard";
import { LocationCard } from "./components/LocationCard";
import { MessagePill } from "./components/MessagePill";
import { ProgramGallery } from "./components/ProgramGallery";
import { buildLocationLabel, Subline } from "./components/Subline";
import { TeamCard } from "./components/TeamCard";

type ProgramDetail = Program &
  ProgramsCardPhotosTrait &
  ProgramsCardLocationsTrait &
  ProgramsCardMapLocationsTrait &
  ProgramsCardAccountWithMembersTrait &
  ProgramsCardDescriptionTrait &
  ProgramsCardAvailabilityTrait;

export interface ShowViewProps {
  program: ProgramDetail;
  /** The running community's celebration wall (community-wide, newest first). */
  celebrations: CelebrationsRow[];
  /** Path to the running community's public profile. */
  profileHref: string;
  /** Path that opens (or starts) a chat with the running community. */
  messageHref: string;
  /** Live favourite state, owned by the page (optimistic). */
  favorited?: boolean;
  shared?: ShareMethod;
  onFavoriteToggle?: () => void;
  onShare?: () => void;
}

// Separator in the jump bar's "community • location" subtitle (not translatable copy).
const BULLET = "•";

/** Gap kept between the floating jump bar and the section scrolled to it. */
const JUMP_BAR_SCROLL_GAP = 16;
/** Jump offset while the floating bar hasn't rendered yet (top-4 + bar + gap). */
const JUMP_BAR_FALLBACK_OFFSET = 108;

const showView = tv({
  slots: {
    columns: "mt-6 flex flex-col md:mt-8 md:flex-row md:items-start md:gap-12",
    main: "md:min-w-0 md:flex-1",
    // Fixed 37% so the sidebar lines up under the two stacked gallery images.
    // Sticky so the actions stay at hand while the main column scrolls; the
    // jump bar lives in the main column, so no space is reserved for it here.
    side: "hidden md:sticky md:top-4 md:block md:w-[37%] md:shrink-0",
    // Content type-scale steps up at xl, not md: through the "midway" range we
    // keep the desktop two-column layout but the mobile type sizes.
    name: "font-extrabold text-[30px] text-neutral-900 leading-10 tracking-[-0.3px] xl:text-display-md",
    // Mobile bottom padding is the gap to the jump strip below — it lives here
    // rather than on the strip so the pinned strip hugs the top bar.
    subline: "mt-2 pb-3 md:pb-0 xl:mt-3",
    // Pins under the always-sticky mobile top bar (the Frame scroller starts
    // below it). Sits in the chrome layer, not below it: the top bar's own
    // scroll gradient is trapped inside that bar's stacking context, so the
    // only way to keep it off the tabs is to cover it — equal z plus later
    // DOM order. The strip then carries the gradient for the whole stack.
    jumpNav: "sticky top-0 z-(--z-chrome-top) -mx-5 md:hidden",
    // Zero-height sticky inside the main column: pins at the viewport top
    // like a fixed bar (it only mounts once the reader is past its flow
    // position) but takes the column's width and never covers the sidebar.
    jumpBarSticky: "sticky top-4 z-30 hidden h-0 md:block",
    // Fades the column's content out as it scrolls under the bar: solid at
    // the viewport top (the wrapper sits at top-4, hence -top-4), clear a
    // little below the bar so content re-emerges gradually.
    jumpBarScrim:
      "pointer-events-none absolute -inset-x-2 -top-4 h-32 bg-gradient-to-b from-40% from-white to-transparent",
    // Jump targets: focused programmatically after a jump, so no focus ring.
    section: "outline-none",
    tagline:
      "font-medium text-lg text-neutral-900 leading-6 tracking-[-0.18px] xl:font-bold xl:text-2xl xl:leading-8 xl:tracking-[-0.24px]",
    description:
      "mt-4 space-y-3 text-base text-neutral-900 leading-6 tracking-[-0.16px] xl:mt-5 xl:space-y-4 xl:text-xl xl:leading-7 xl:tracking-normal",
  },
});

export function ShowView({
  program,
  celebrations,
  profileHref,
  messageHref,
  favorited,
  shared = false,
  onFavoriteToggle,
  onShare,
}: ShowViewProps) {
  const styles = showView();
  const reducedMotion = useReducedMotion() ?? false;

  const images = program.photos.map((photo) => photo.image).filter((image): image is ImageType => image !== null);
  const location = program.locations[0] ?? null;
  const hasInfluences = hasKnownInfluences(program.influences);
  const hasTeam = program.account.members.length > 0;
  const hasMapCard = program.map_locations.length > 0;
  const hasCelebrations = celebrations.length > 0;

  // Sections render once (shared across breakpoints), so the Mapbox singleton
  // has a single consumer — mapActive only needs the mount gate (SSR and
  // hydration render no live map, keeping server and first client HTML
  // identical).
  const hasMounted = useHasMounted();

  const sections: SectionJumpSection[] = [
    { key: "summary", label: t("components.views.programs.show.tab_summary") },
    ...(hasMapCard ? [{ key: "location", label: t("components.views.programs.show.tab_location") }] : []),
    ...(hasInfluences ? [{ key: "influences", label: t("components.views.programs.show.tab_influences") }] : []),
    ...(hasTeam ? [{ key: "team", label: t("components.views.programs.show.tab_team") }] : []),
    ...(hasCelebrations ? [{ key: "celebrations", label: t("components.views.programs.show.tab_celebrations") }] : []),
  ];

  const jumpNavRef = useRef<HTMLDivElement>(null);
  const jumpBarRef = useRef<HTMLDivElement>(null);
  const getTopOffset = useCallback(() => {
    // Mobile: the pinned strip's height (offsetParent is null while it is
    // display:none at md+). Desktop: the floating bar's bottom edge plus a gap,
    // or a ballpark before the bar has appeared.
    const strip = jumpNavRef.current;
    if (strip?.offsetParent) return strip.getBoundingClientRect().height;
    const bar = jumpBarRef.current;
    if (bar) return bar.getBoundingClientRect().bottom + JUMP_BAR_SCROLL_GAP;
    return JUMP_BAR_FALLBACK_OFFSET;
  }, []);
  const { activeKey, pastFirstSection, registerSection, scrollToSection } = useScrollSpy({
    keys: sections.map(({ key }) => key),
    getTopOffset,
  });
  // Implicit viewport root, as in Frame: on mobile the scroller's box starts
  // below the top bar and clips the sentinel there, so it stops intersecting
  // exactly as the strip pins.
  const [jumpNavSentinelRef, jumpNavInFlow] = useScrollSentinel({ position: "top", threshold: 0 });
  const isDesktop = useBreakpoint("md");

  const communityName = program.account.display_name ?? program.account.handle ?? "";
  const barSubtitle = [communityName, buildLocationLabel(location)].filter(Boolean).join(` ${BULLET} `) || undefined;

  const summaryContent = (
    <>
      <Highlights
        dropOffAvailable={program.drop_off_available}
        feature={program.account.feature}
        maxAge={program.max_age}
        minAge={program.min_age}
      />
      {program.tagline && <p className={`${styles.tagline()} mt-6 xl:mt-8`}>{program.tagline}</p>}
      {program.description && (
        <div className={styles.description()}>
          <RichText>{program.description}</RichText>
        </div>
      )}
    </>
  );

  return (
    <div>
      <ProgramGallery images={images} />

      <div className={styles.columns()}>
        {/* Main column */}
        <div className={styles.main()}>
          {/* Desktop: floating jump bar once the reader is into the sections.
              pastFirstSection (not a viewport-top sentinel) so a jump back to
              Summary — which lands it exactly on the offset line — keeps the
              bar the reader just used. The breakpoint gate keeps the bar
              unmounted on mobile, where the strip owns navigation. Lives at
              the very top of the column so that whenever it is mounted its
              flow position is already scrolled past — i.e. it is always in
              the pinned state, never drawn in place. */}
          <div className={styles.jumpBarSticky()}>
            <AnimatePresence>
              {isDesktop && pastFirstSection && (
                <motion.div
                  animate={{ opacity: 1, y: 0 }}
                  className="relative"
                  exit={{ opacity: 0, y: -8 }}
                  initial={{ opacity: 0, y: -8 }}
                  ref={jumpBarRef}
                  transition={{ duration: reducedMotion ? 0 : 0.18, ease: "easeOut" }}
                >
                  <div aria-hidden className={styles.jumpBarScrim()} />
                  <SectionJumpBar
                    className="relative"
                    activeKey={activeKey}
                    onSelect={scrollToSection}
                    sections={sections}
                    subtitle={barSubtitle}
                    title={program.name}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <h1 className={styles.name()}>{program.name}</h1>
          <Subline
            account={program.account}
            className={styles.subline()}
            languages={program.instruction_languages}
            location={location}
            profileHref={profileHref}
          />

          {/* Mobile: quick-jump strip, pinned under the top bar while scrolling.
              Its gradient stands in for the top bar's while pinned, so the
              chrome stack keeps a single edge at its bottom. */}
          <div aria-hidden className="h-0" ref={jumpNavSentinelRef} />
          <div className={styles.jumpNav()} ref={jumpNavRef}>
            <SectionJumpNav activeKey={activeKey} onSelect={scrollToSection} sections={sections} />
            <TopBarScrollShadow visible={!jumpNavInFlow} />
          </div>

          <section
            className={styles.section({ className: "mt-6 md:mt-8" })}
            ref={registerSection("summary")}
            tabIndex={-1}
          >
            {summaryContent}
            <MessagePill className="mt-8 md:hidden" href={messageHref} size="sm" />
          </section>
          {hasMapCard && (
            <section className={styles.section({ className: "mt-10" })} ref={registerSection("location")} tabIndex={-1}>
              <LocationCard mapActive={hasMounted} mapLocations={program.map_locations} />
            </section>
          )}
          {hasInfluences && (
            <section
              className={styles.section({ className: "mt-6" })}
              ref={registerSection("influences")}
              tabIndex={-1}
            >
              <InfluencesCard influences={program.influences} />
            </section>
          )}
          {hasTeam && (
            <section className={styles.section({ className: "mt-6" })} ref={registerSection("team")} tabIndex={-1}>
              <TeamCard members={program.account.members} />
            </section>
          )}
          {hasCelebrations && (
            <section
              className={styles.section({ className: "mt-10" })}
              ref={registerSection("celebrations")}
              tabIndex={-1}
            >
              <CelebrationsCard celebrations={celebrations} communityName={communityName} />
            </section>
          )}
        </div>

        {/* Side column — desktop only. Steps down between desktop and mobile at
            the lg breakpoint, mirroring the product page's action bar. */}
        <div className={styles.side()}>
          {/* Compact action pill for md–lg */}
          <ActionPill className="xl:hidden" size="32">
            <ActionPill.ToggleButton
              aria-label={t("components.views.programs.show.toggle_favorites")}
              hoverColor="pink"
              icon={<Icon name="heartInactive" shadow="none" type="sticker" />}
              isSelected={favorited}
              onChange={onFavoriteToggle}
              selectedIcon={<Icon name="heart" shadow="none" type="sticker" />}
              size="32"
            >
              {({ isSelected }) =>
                isSelected
                  ? t("components.views.programs.show.favorited")
                  : t("components.views.programs.show.favorite")
              }
            </ActionPill.ToggleButton>

            <ActionPill.Button
              hoverColor="yellow"
              icon={<Icon name="share" shadow="none" type="sticker" />}
              onPress={onShare}
              size="32"
            >
              {shared === "clipboard"
                ? t("components.views.programs.show.copied")
                : t("components.views.programs.show.share")}
            </ActionPill.Button>
          </ActionPill>

          {/* Full action pill for lg+ */}
          <ActionPill className="hidden xl:flex">
            <ActionPill.ToggleButton
              aria-label={t("components.views.programs.show.toggle_favorites")}
              hoverColor="pink"
              icon={<Icon name="heartInactive" shadow="none" type="sticker" />}
              isSelected={favorited}
              onChange={onFavoriteToggle}
              selectedIcon={<Icon name="heart" shadow="none" type="sticker" />}
            >
              {({ isSelected }) =>
                isSelected
                  ? t("components.views.programs.show.added_to_favorites")
                  : t("components.views.programs.show.add_to_favorites")
              }
            </ActionPill.ToggleButton>

            <ActionPill.Button
              hoverColor="yellow"
              icon={<Icon name="share" shadow="none" type="sticker" />}
              onPress={onShare}
            >
              {shared === "clipboard"
                ? t("components.views.programs.show.link_copied")
                : t("components.views.programs.show.share_with_friends")}
            </ActionPill.Button>
          </ActionPill>

          <AvailabilityCard
            className="mt-4"
            messageHref={messageHref}
            mode={program.availability_mode}
            sessions={program.sessions}
          />

          <MessagePill className="mt-4 xl:hidden" href={messageHref} size="sm" />
          <MessagePill className="mt-4 hidden xl:flex" href={messageHref} size="lg" />
        </div>
      </div>
    </div>
  );
}
